import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Shell } from "@/components/Shell";
import { UploadDialog } from "@/components/UploadDialog";
import { Dashboard } from "@/pages/Dashboard";
import { ActivityDetail } from "@/pages/ActivityDetail";
import { ActivityCompare } from "@/pages/ActivityCompare";
import { Login } from "@/pages/Login";
import { Settings } from "@/pages/Settings";
import { CalendarPage } from "@/pages/Calendar";
import { ProgressPage } from "@/pages/Progress";
import { CoachingHome } from "@/pages/CoachingHome";
import { WorkoutsPage } from "@/pages/Workouts";
import { AdminPage } from "@/pages/Admin";
import { ActivePlanSyncBridge } from "@/components/ActivePlanSyncBridge";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Loader2 } from "lucide-react";

const Protected = ({ children }) => {
  const { user, loading, error, refresh } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-dark-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
      </div>
    );
  }
  if (error === "network" || error === "server") {
    const messages = {
      network: "Could not reach the server. Make sure the backend is running.",
      server: "Something went wrong while checking your account.",
    };
    return (
      <div className="min-h-screen bg-dark-background flex items-center justify-center px-6">
        <div className="border border-dark-border bg-dark-surface-1 p-10 text-center max-w-lg">
          <h2 className="font-heading text-2xl tracking-wider uppercase">Unable To Start</h2>
          <p className="text-sm text-dark-text-secondary mt-3">{messages[error]}</p>
          <button
            onClick={refresh}
            className="mt-6 inline-flex items-center gap-2 border border-dark-border hover:border-brand-primary px-6 py-3 text-xs tracking-[0.25em] uppercase font-semibold"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

export const AppRoutes = () => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <Protected>
              <Shell onUploadClick={() => setUploadOpen(true)}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <Dashboard
                        onUploadClick={() => setUploadOpen(true)}
                        refreshKey={refreshKey}
                      />
                    }
                  />
                  <Route path="/activity/:id/compare" element={<ActivityCompare />} />
                  <Route path="/activity/:id" element={<ActivityDetail />} />
                  <Route path="/calendar" element={<CalendarPage refreshKey={refreshKey} />} />
                  <Route path="/workouts" element={<WorkoutsPage />} />
                  <Route path="/progress" element={<ProgressPage />} />
                  <Route path="/coach" element={<CoachingHome />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/admin" element={<AdminPage />} />
                </Routes>
              </Shell>
            </Protected>
          }
        />
      </Routes>
      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={() => setRefreshKey((k) => k + 1)}
      />
    </>
  );
};

const ThemedToaster = () => {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="top-right"
      toastOptions={{
        style: {
          background: "var(--app-surface)",
          border: "1px solid var(--app-border)",
          borderRadius: 0,
          color: "var(--app-text-primary)",
          fontFamily: "Manrope",
        },
      }}
    />
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ActivePlanSyncBridge />
          <AppRoutes />
          <ThemedToaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
